/**
 * MeasurementsSummary - Summary cards component
 * Displays latest measurements with trend indicators
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useTranslation } from "@/hooks/useTranslation";
import type { MeasurementSummary } from "../types";
import { formatDate, formatNumber } from "../utils";

interface MeasurementsSummaryProps {
  summary: MeasurementSummary | null;
}

export function MeasurementsSummary({ summary }: MeasurementsSummaryProps) {
  const { t } = useTranslation();

  if (!summary) return null;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {/* Weight Card */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t("measurements:cards.weight")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{summary.weight || "-"} kg</div>
          <div className="text-xs text-muted-foreground">
            {formatDate(summary.date)}
          </div>
        </CardContent>
      </Card>

      {/* Body Fat Card */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t("measurements:cards.bodyFat")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{summary.bodyFat || "-"}%</div>
          <div className="text-xs text-muted-foreground">
            {formatNumber(summary.fatMassKg)} kg
          </div>
        </CardContent>
      </Card>

      {/* Muscle Mass Card */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t("measurements:cards.muscleMass")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{summary.muscleMass || "-"}%</div>
          <div className="text-xs text-muted-foreground">
            {formatNumber(summary.muscleMassKg)} kg
          </div>
        </CardContent>
      </Card>

      {/* Visceral Fat Card */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t("measurements:cards.visceralFat")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{summary.visceralFat || "-"}</div>
          <div className="text-xs text-muted-foreground">
            {t("measurements:cards.level")}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
